/**
 * Tic-Tac-Toe game board component.
 * Renders the 3x3 grid with animations and winning line highlight.
 */

import { type Player, type Board, type Cell } from "@/lib/tictactoe";

interface TicTacToeBoardProps {
  board: Board;
  winningLine: number[] | null;
  isMyTurn: boolean;
  myTeam: "a" | "b";
  onCellClick: (cell: number) => void;
  disabled?: boolean;
}

export function TicTacToeBoard({
  board,
  winningLine,
  isMyTurn,
  myTeam,
  onCellClick,
  disabled = false,
}: TicTacToeBoardProps) {
  const isWinningCell = (index: number) => winningLine?.includes(index) ?? false;

  const handleClick = (index: number) => {
    if (disabled || !isMyTurn || board[index] !== null) return;
    onCellClick(index);
  };

  return (
    <div className="mx-auto w-full max-w-[320px]">
      <div className="grid grid-cols-3 gap-1.5">
        {board.map((cell, index) => (
          <button
            key={index}
            onClick={() => handleClick(index)}
            disabled={disabled || !isMyTurn || cell !== null}
            className={`aspect-square rounded-xl border-2 text-4xl font-bold transition-all duration-200 sm:text-5xl ${
              isWinningCell(index)
                ? "border-green-500 bg-green-500/10 text-green-500"
                : cell
                  ? "border-border bg-card text-foreground"
                  : isMyTurn && !disabled
                    ? "border-border bg-card hover:border-primary/50 hover:bg-accent cursor-pointer"
                    : "border-border bg-card text-muted-foreground/30 cursor-not-allowed"
            } ${
              // Entrance animation for placed marks
              cell && !isWinningCell(index) ? "animate-[pop_200ms_ease-out]" : ""
            }`}
          >
            {cell === "a" && (
              <span className={isWinningCell(index) ? "text-green-500" : "text-blue-500"}>
                X
              </span>
            )}
            {cell === "b" && (
              <span className={isWinningCell(index) ? "text-green-500" : "text-red-500"}>
                O
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Turn indicator */}
      {!disabled && (
        <div className="mt-4 text-center">
          {isMyTurn ? (
            <p className="text-sm font-medium text-green-500">
              Your turn — place your mark
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Waiting for opponent...
            </p>
          )}
        </div>
      )}
    </div>
  );
}